// CreateSearchFolderDialog — modal for creating a Microsoft Graph
// search folder from a small set of presets. Pure-UI; the parent
// component owns the api.createSearchFolder call.
//
// Presets are intentionally narrow in v1:
//   * From sender — pin all email from a specific email address
//   * Anything with this contact — sent or received with one address
//   * Unread across mailbox — useful "what do I need to look at"
//   * Flagged across mailbox — followups the advisor flagged
// Raw OData filterQuery is supported by the backend but not exposed
// in the picker — keeps the surface area small.

import { useEffect, useState } from "react";

import { Button } from "../../ui";
import type { CreateSearchFolderInput } from "../../api";

export interface CreateSearchFolderDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: CreateSearchFolderInput) => void;
  busy?: boolean;
  // Default email address — surfaces when invoked from a contact
  // page so the picker pre-fills "Anything with this contact".
  defaultEmailAddress?: string | null;
}

type PresetKind = "from_sender" | "from_or_to" | "unread" | "flagged";

const PRESET_LABELS: Record<PresetKind, { title: string; sublabel: string }> = {
  from_or_to: {
    title: "All email with a contact",
    sublabel: "Sent or received with this email address",
  },
  from_sender: {
    title: "From a specific sender",
    sublabel: "Only inbound from this email address",
  },
  unread: {
    title: "Unread across mailbox",
    sublabel: "Everything you haven't read yet",
  },
  flagged: {
    title: "Flagged for follow-up",
    sublabel: "Anything you've starred / flagged in Outlook",
  },
};

function defaultName(preset: PresetKind, emailAddress: string): string {
  switch (preset) {
    case "from_or_to":
      return emailAddress ? `With ${emailAddress}` : "With contact";
    case "from_sender":
      return emailAddress ? `From ${emailAddress}` : "From sender";
    case "unread":
      return "Unread";
    case "flagged":
      return "Flagged";
  }
}

export function CreateSearchFolderDialog({
  open,
  onClose,
  onConfirm,
  busy,
  defaultEmailAddress,
}: CreateSearchFolderDialogProps) {
  const [preset, setPreset] = useState<PresetKind>(
    defaultEmailAddress ? "from_or_to" : "unread",
  );
  const [emailAddress, setEmailAddress] = useState(defaultEmailAddress ?? "");
  const [displayName, setDisplayName] = useState(
    defaultName(
      defaultEmailAddress ? "from_or_to" : "unread",
      defaultEmailAddress ?? "",
    ),
  );
  const [scope, setScope] = useState<"inbox" | "all">("all");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const initialPreset: PresetKind = defaultEmailAddress
      ? "from_or_to"
      : "unread";
    setPreset(initialPreset);
    setEmailAddress(defaultEmailAddress ?? "");
    setDisplayName(defaultName(initialPreset, defaultEmailAddress ?? ""));
    setScope("all");
    setError(null);
  }, [open, defaultEmailAddress]);

  if (!open) return null;

  const handlePresetChange = (next: PresetKind) => {
    setPreset(next);
    // Re-derive a sensible default name when switching presets.
    setDisplayName(defaultName(next, emailAddress));
  };

  const handleEmailChange = (value: string) => {
    setEmailAddress(value);
    if (preset === "from_or_to" || preset === "from_sender") {
      setDisplayName(defaultName(preset, value));
    }
  };

  const handleConfirm = () => {
    setError(null);
    if (!displayName.trim()) {
      setError("Folder name is required.");
      return;
    }
    if (preset === "from_or_to" || preset === "from_sender") {
      const trimmed = emailAddress.trim();
      if (!trimmed) {
        setError("Email address is required for this preset.");
        return;
      }
      // Cheap shape check — Graph rejects anything obviously invalid.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setError("Enter a valid email address.");
        return;
      }
    }
    let payload: CreateSearchFolderInput;
    switch (preset) {
      case "from_or_to":
        payload = {
          displayName: displayName.trim(),
          preset: "from_or_to",
          emailAddress: emailAddress.trim(),
          scope,
        };
        break;
      case "from_sender":
        payload = {
          displayName: displayName.trim(),
          preset: "from_sender",
          emailAddress: emailAddress.trim(),
          scope,
        };
        break;
      case "unread":
        payload = {
          displayName: displayName.trim(),
          preset: "unread",
          scope,
        };
        break;
      case "flagged":
        payload = {
          displayName: displayName.trim(),
          preset: "flagged",
          scope,
        };
        break;
    }
    onConfirm(payload);
  };

  const showsEmailField = preset === "from_or_to" || preset === "from_sender";

  return (
    <div className="schedule-send-overlay" role="dialog" aria-modal="true">
      <div className="schedule-send-card">
        <header className="schedule-send-header">
          <h3>New search folder</h3>
          <button
            type="button"
            className="schedule-send-close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>
        <div className="schedule-send-body">
          <ul className="schedule-send-presets">
            {(["from_or_to", "from_sender", "unread", "flagged"] as PresetKind[]).map(
              (k) => (
                <li key={k}>
                  <label className="schedule-send-preset-row">
                    <input
                      type="radio"
                      name="search-folder-preset"
                      value={k}
                      checked={preset === k}
                      onChange={() => handlePresetChange(k)}
                      disabled={busy}
                    />
                    <span className="schedule-send-preset-label">
                      {PRESET_LABELS[k].title}
                    </span>
                    <span className="schedule-send-preset-sublabel">
                      {PRESET_LABELS[k].sublabel}
                    </span>
                  </label>
                </li>
              ),
            )}
          </ul>
          {showsEmailField ? (
            <div className="schedule-send-custom">
              <label htmlFor="search-folder-email">Email address</label>
              <input
                id="search-folder-email"
                type="email"
                value={emailAddress}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="name@example.com"
                disabled={busy}
              />
            </div>
          ) : null}
          <div className="schedule-send-custom">
            <label htmlFor="search-folder-name">Folder name</label>
            <input
              id="search-folder-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={256}
              disabled={busy}
            />
          </div>
          <div className="schedule-send-custom">
            <label htmlFor="search-folder-scope">Search where</label>
            <select
              id="search-folder-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as "inbox" | "all")}
              disabled={busy}
            >
              <option value="all">Entire mailbox (Inbox + Sent + Archive…)</option>
              <option value="inbox">Inbox only (and its sub-folders)</option>
            </select>
          </div>
          {error ? (
            <p className="schedule-send-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <footer className="schedule-send-footer">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={busy}>
            {busy ? "Creating…" : "Create folder"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
